/**
 * ProjectDataTables - Data tables for structured project information
 * Allows creating, editing, and managing tabular data within projects
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Plus, 
  Trash2, 
  Edit2, 
  Save, 
  X, 
  Table, 
  FileSpreadsheet,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Copy,
  Download,
  Upload,
  MoreVertical,
  Check,
  Clock,
  AlertCircle,
  Bookmark,
  FileText,
  Loader2,
  ListTree,
  Hammer
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { getApiUrl } from '../../utils/api';

// Status options with icons and colors
const STATUS_OPTIONS = [
  { value: 'pending', labelKey: 'dataTables.status.pending', icon: Clock, color: 'text-yellow-600 bg-yellow-100' },
  { value: 'done', labelKey: 'dataTables.status.done', icon: Check, color: 'text-green-600 bg-green-100' },
  { value: 'cancelled', labelKey: 'dataTables.status.cancelled', icon: X, color: 'text-red-600 bg-red-100' },
  { value: 'in_progress', labelKey: 'dataTables.status.in_progress', icon: Clock, color: 'text-blue-600 bg-blue-100' }
];

// Column type options
const COLUMN_TYPES = [
  { value: 'text', labelKey: 'dataTables.columnTypes.text' },
  { value: 'number', labelKey: 'dataTables.columnTypes.number' },
  { value: 'date', labelKey: 'dataTables.columnTypes.date' },
  { value: 'status', labelKey: 'dataTables.columnTypes.status' },
  { value: 'checkbox', labelKey: 'dataTables.columnTypes.checkbox' }
];

// Status Cell Component
const StatusCell = ({ value, onChange, readOnly, t }) => {
  const [isOpen, setIsOpen] = useState(false);
  const status = STATUS_OPTIONS.find(s => s.value === value) || STATUS_OPTIONS[0];
  const Icon = status.icon;

  if (readOnly) {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
        <Icon className="w-3 h-3" />
        {t(status.labelKey)}
      </span>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${status.color} hover:opacity-80`}
      >
        <Icon className="w-3 h-3" />
        {t(status.labelKey)}
        <ChevronDown className="w-3 h-3" />
      </button>
      
      {isOpen && (
        <div className="absolute z-10 mt-1 bg-white border rounded-lg shadow-lg py-1 min-w-[120px]">
          {STATUS_OPTIONS.map(opt => {
            const OptIcon = opt.icon;
            return (
              <button
                key={opt.value}
                onClick={() => {
                  onChange(opt.value);
                  setIsOpen(false);
                }}
                className={`w-full px-3 py-1.5 text-left text-sm hover:bg-gray-50 flex items-center gap-2 ${
                  opt.value === value ? 'bg-gray-100' : ''
                }`}
              >
                <OptIcon className={`w-3 h-3 ${opt.color.split(' ')[0]}`} />
                {t(opt.labelKey)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Editable Cell Component
const EditableCell = ({ value, type, onChange, columnId, readOnly }) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');

  useEffect(() => {
    setEditValue(value || '');
  }, [value]);

  const handleSave = () => {
    onChange(columnId, editValue);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') {
      setEditValue(value || '');
      setIsEditing(false);
    }
  };

  if (type === 'status') {
    return (
      <StatusCell 
        value={value} 
        onChange={(newVal) => onChange(columnId, newVal)}
        readOnly={readOnly}
        t={t}
      />
    );
  }

  if (type === 'checkbox') {
    return (
      <input
        type="checkbox"
        checked={value === true || value === 'true'}
        onChange={(e) => onChange(columnId, e.target.checked)}
        disabled={readOnly}
        className="w-4 h-4 text-emerald-600 rounded"
      />
    );
  }

  if (readOnly) {
    return <span className="text-sm">{value || '-'}</span>;
  }

  if (isEditing) {
    return (
      <input
        type={type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        autoFocus
        className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-emerald-500"
      />
    );
  }

  return (
    <div
      onClick={() => setIsEditing(true)}
      className="cursor-pointer px-2 py-1 hover:bg-gray-50 rounded min-h-[28px] text-sm"
    >
      {value || <span className="text-gray-400">-</span>}
    </div>
  );
};

// Single Data Table Component
const DataTableView = ({ table, onUpdate, onDelete, onSaveAsTemplate, currentUser }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isEditingName, setIsEditingName] = useState(false);
  const [tableName, setTableName] = useState(table.name);
  const [showColumnForm, setShowColumnForm] = useState(false);
  const [newColumn, setNewColumn] = useState({ name: '', type: 'text' });
  const [rows, setRows] = useState(table.rows || []);
  const [columns, setColumns] = useState(table.columns || []);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setRows(table.rows || []);
    setColumns(table.columns || []);
    setTableName(table.name);
  }, [table]);

  const handleAddColumn = async () => {
    if (!newColumn.name.trim()) return;
    
    const column = {
      id: `col-${Date.now()}`,
      name: newColumn.name,
      type: newColumn.type,
      order: columns.length,
      options: newColumn.type === 'status' ? STATUS_OPTIONS.map(s => s.value) : null
    };
    
    const updatedColumns = [...columns, column];
    setColumns(updatedColumns);
    
    // Save to backend
    try {
      await fetch(`${getApiUrl()}/projects/${table.project_id}/tables/${table.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ columns: updatedColumns })
      });
    } catch (error) {
      console.error('Error adding column:', error);
    }
    
    setNewColumn({ name: '', type: 'text' });
    setShowColumnForm(false);
  };

  const handleDeleteColumn = async (columnId) => {
    const updatedColumns = columns.filter(c => c.id !== columnId);
    setColumns(updatedColumns);
    
    // Also remove column data from rows
    const updatedRows = rows.map(row => {
      const { [columnId]: removed, ...rest } = row.values;
      return { ...row, values: rest };
    });
    setRows(updatedRows);
    
    try {
      await fetch(`${getApiUrl()}/projects/${table.project_id}/tables/${table.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ columns: updatedColumns, rows: updatedRows })
      });
    } catch (error) {
      console.error('Error deleting column:', error);
    }
  };

  const handleMoveColumn = async (columnId, direction) => {
    const currentIndex = columns.findIndex(c => c.id === columnId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'left' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= columns.length) return;
    
    // Swap columns
    const updatedColumns = [...columns];
    [updatedColumns[currentIndex], updatedColumns[newIndex]] = [updatedColumns[newIndex], updatedColumns[currentIndex]];
    
    // Update order values
    updatedColumns.forEach((col, idx) => {
      col.order = idx;
    });
    
    setColumns(updatedColumns);
    
    // Save to backend
    try {
      await fetch(`${getApiUrl()}/projects/${table.project_id}/tables/${table.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ columns: updatedColumns })
      });
    } catch (error) {
      console.error('Error moving column:', error);
    }
  };

  const handleAddRow = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(
        `${getApiUrl()}/projects/${table.project_id}/tables/${table.id}/rows?user_id=${currentUser.id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ values: {} })
        }
      );
      const newRow = await response.json();
      setRows([...rows, newRow]);
    } catch (error) {
      console.error('Error adding row:', error);
    }
    setIsSaving(false);
  };

  const handleCellChange = async (rowId, columnId, value) => {
    const updatedRows = rows.map(row => {
      if (row.id === rowId) {
        return {
          ...row,
          values: { ...row.values, [columnId]: value }
        };
      }
      return row;
    });
    setRows(updatedRows);
    
    // Debounced save
    try {
      const row = updatedRows.find(r => r.id === rowId);
      await fetch(
        `${getApiUrl()}/projects/${table.project_id}/tables/${table.id}/rows/${rowId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ values: row.values })
        }
      );
    } catch (error) {
      console.error('Error updating cell:', error);
    }
  };

  const handleDeleteRow = async (rowId) => {
    try {
      await fetch(
        `${getApiUrl()}/projects/${table.project_id}/tables/${table.id}/rows/${rowId}`,
        { method: 'DELETE' }
      );
      setRows(rows.filter(r => r.id !== rowId));
    } catch (error) {
      console.error('Error deleting row:', error);
    }
  };

  const handleSaveName = async () => {
    try {
      await fetch(`${getApiUrl()}/projects/${table.project_id}/tables/${table.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: tableName })
      });
      setIsEditingName(false);
    } catch (error) {
      console.error('Error updating name:', error);
    }
  };

  return (
    <div className="border rounded-lg mb-4 bg-white shadow-sm">
      {/* Table Header */}
      <div className="flex items-center justify-between p-3 bg-gray-50 border-b">
        <div className="flex items-center gap-2">
          <button onClick={() => setIsExpanded(!isExpanded)} className="text-gray-500 hover:text-gray-700">
            {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </button>
          <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
          
          {isEditingName ? (
            <div className="flex items-center gap-2">
              <Input
                value={tableName}
                onChange={(e) => setTableName(e.target.value)}
                className="h-8 w-48"
                autoFocus
              />
              <Button size="sm" variant="ghost" onClick={handleSaveName}>
                <Save className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setIsEditingName(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <span 
              className="font-semibold text-gray-800 cursor-pointer hover:text-emerald-600"
              onClick={() => setIsEditingName(true)}
            >
              {tableName}
            </span>
          )}
          
          <span className="text-xs text-gray-500 ml-2">
            {rows.length} {rows.length === 1 ? t('dataTables.row') : t('dataTables.rows')}
          </span>
        </div>
        
        <div className="flex items-center gap-1">
          {/* Export Buttons */}
          <div className="relative group">
            <Button 
              size="sm" 
              variant="ghost" 
              className="text-green-600 hover:text-green-700 hover:bg-green-50"
              title={t('dataTables.exportOptions', 'Export-Optionen')}
            >
              <FileSpreadsheet className="w-4 h-4" />
              <ChevronDown className="w-3 h-3 ml-0.5" />
            </Button>
            <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg py-1 hidden group-hover:block z-20 min-w-[180px]">
              <button
                onClick={() => window.open(`${getApiUrl()}/projects/${table.project_id}/tables/${table.id}/export/excel`, '_blank')}
                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
              >
                <FileSpreadsheet className="w-4 h-4 text-green-600" />
                {t('dataTables.exportExcel', 'Excel Export')}
              </button>
              {table.original_file_key && (
                <button
                  onClick={() => window.open(`${getApiUrl()}/projects/${table.project_id}/tables/${table.id}/export/excel-template`, '_blank')}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                >
                  <FileSpreadsheet className="w-4 h-4 text-blue-600" />
                  {t('dataTables.exportWithTemplate', 'Mit Vorlage exportieren')}
                </button>
              )}
              <button
                onClick={() => window.open(`${getApiUrl()}/projects/${table.project_id}/tables/${table.id}/export/csv`, '_blank')}
                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
              >
                <FileText className="w-4 h-4 text-gray-600" />
                {t('dataTables.exportCsv', 'CSV Export')}
              </button>
              <button
                onClick={() => window.open(`${getApiUrl()}/projects/${table.project_id}/tables/${table.id}/export/pdf`, '_blank')}
                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
              >
                <Download className="w-4 h-4 text-red-600" />
                {t('dataTables.exportPdf', 'PDF Export')}
              </button>
            </div>
          </div>
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={() => onSaveAsTemplate(table)}
            title={t('dataTables.saveAsTemplate')}
          >
            <Bookmark className="w-4 h-4" />
          </Button>
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={() => onDelete(table.id)}
            className="text-red-500 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Table Content */}
      {isExpanded && (
        <div className="p-3">
          {/* Scroll hint for tables with many columns */}
          {columns.length > 3 && (
            <p className="text-xs text-gray-400 mb-2">
              💡 {t('dataTables.scrollHintDetailed', 'Shift + Scrollrad oder Touchpad für horizontales Scrollen')}
            </p>
          )}
          <div 
            className="overflow-x-auto pb-2 scrollbar-thin" 
            style={{ 
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'thin',
              scrollbarColor: '#94a3b8 #f1f5f9'
            }}
          >
            <table className="border-collapse" style={{ minWidth: 'max-content' }}>
              <thead>
                <tr className="bg-gray-100">
                  {columns.map((col, idx) => (
                    <th key={col.id} className="border px-3 py-2 text-left text-sm font-medium text-gray-700 group relative whitespace-nowrap" style={{ minWidth: '120px' }}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium">{col.name}</span>
                        <div className="flex items-center gap-0.5">
                          {/* Move left */}
                          {idx > 0 && (
                            <button
                              onClick={() => handleMoveColumn(col.id, 'left')}
                              className="p-0.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded"
                              title="Move left"
                            >
                              <ChevronLeft className="w-3 h-3" />
                            </button>
                          )}
                          {/* Move right */}
                          {idx < columns.length - 1 && (
                            <button
                              onClick={() => handleMoveColumn(col.id, 'right')}
                              className="p-0.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded"
                              title="Move right"
                            >
                              <ChevronRight className="w-3 h-3" />
                            </button>
                          )}
                          {/* Delete column */}
                          <button
                            onClick={() => handleDeleteColumn(col.id)}
                            className="p-0.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded ml-1"
                            title="Delete column"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      <span className="text-xs text-gray-400 font-normal">{t(`dataTables.columnTypes.${col.type}`)}</span>
                    </th>
                  ))}
                  <th className="border px-3 py-2 w-20">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowColumnForm(true)}
                      className="text-emerald-600"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  // Check for Bedarfsposition/Alternativposition meta
                  const isBedarfs = row.meta?.is_bedarfsposition;
                  const isAlternativ = row.meta?.is_alternativposition;
                  
                  let rowClass = "hover:bg-gray-50 group";
                  if (isBedarfs) rowClass = "bg-orange-50 hover:bg-orange-100 group border-l-4 border-orange-400";
                  else if (isAlternativ) rowClass = "bg-yellow-50 hover:bg-yellow-100 group border-l-4 border-yellow-400";
                  
                  return (
                  <tr key={row.id} className={rowClass}>
                    {columns.map((col) => (
                      <td key={`${row.id}-${col.id}`} className="border px-2 py-1">
                        <EditableCell
                          value={row.values?.[col.id]}
                          type={col.type}
                          columnId={col.id}
                          onChange={(colId, val) => handleCellChange(row.id, colId, val)}
                        />
                      </td>
                    ))}
                    <td className="border px-2 py-1 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {isBedarfs && <span className="text-orange-500 text-xs" title="Bedarfsposition">⚡</span>}
                        {isAlternativ && <span className="text-yellow-500 text-xs" title="Alternativposition">🔄</span>}
                        <button
                          onClick={() => handleDeleteRow(row.id)}
                          className="text-gray-400 hover:text-red-500 p-1 rounded hover:bg-red-50"
                          title={t('dataTables.deleteRow')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Add Row Button */}
          <Button
            size="sm"
            variant="outline"
            onClick={handleAddRow}
            disabled={isSaving}
            className="mt-2"
          >
            <Plus className="w-4 h-4 mr-1" />
            {t('dataTables.addRow')}
          </Button>

          {/* Add Column Form */}
          {showColumnForm && (
            <div className="mt-3 p-3 bg-gray-50 rounded-lg border">
              <h4 className="font-medium text-sm mb-2">{t('dataTables.addColumn')}</h4>
              <div className="flex items-center gap-2">
                <Input
                  value={newColumn.name}
                  onChange={(e) => setNewColumn({ ...newColumn, name: e.target.value })}
                  placeholder={t('dataTables.columnName')}
                  className="h-8"
                />
                <select
                  value={newColumn.type}
                  onChange={(e) => setNewColumn({ ...newColumn, type: e.target.value })}
                  className="h-8 px-2 border rounded text-sm"
                >
                  {COLUMN_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{t(type.labelKey)}</option>
                  ))}
                </select>
                <Button size="sm" onClick={handleAddColumn}>
                  <Plus className="w-4 h-4 mr-1" />
                  {t('dataTables.add')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowColumnForm(false)}>
                  {t('dataTables.cancel')}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Mapping of system template names to i18n keys
const SYSTEM_TEMPLATE_KEYS = {
  'Material-Bestellung': 'materialOrder',
  'Material Order': 'materialOrder',
  'Mängelliste': 'defectList',
  'Defect List': 'defectList',
  'Aufgaben-Checkliste': 'taskChecklist',
  'Task Checklist': 'taskChecklist',
  'Arbeitsstunden': 'workHours',
  'Work Hours': 'workHours',
  'Lieferungen': 'deliveries',
  'Deliveries': 'deliveries',
  'Container Import China': 'containerChina',
};

// Helper to get translated template name/description
const getTranslatedTemplate = (template, t) => {
  const key = SYSTEM_TEMPLATE_KEYS[template.name];
  if (key) {
    const translatedName = t(`dataTables.systemTemplates.${key}.name`, { defaultValue: template.name });
    const translatedDesc = t(`dataTables.systemTemplates.${key}.description`, { defaultValue: template.description });
    return { name: translatedName, description: translatedDesc };
  }
  return { name: template.name, description: template.description };
};

// Template Selection Modal
const TemplateModal = ({ isOpen, onClose, onSelect, templates, t }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{t('dataTables.selectTemplate')}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {templates.length === 0 ? (
          <p className="text-gray-500 text-center py-4">{t('dataTables.noTemplates')}</p>
        ) : (
          <div className="space-y-2">
            {templates.map(template => {
              const translated = getTranslatedTemplate(template, t);
              return (
                <button
                  key={template.id}
                  onClick={() => onSelect(template)}
                  className="w-full p-3 text-left border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{template.icon || '📊'}</span>
                    <div>
                      <div className="font-medium">{translated.name}</div>
                      {translated.description && (
                        <div className="text-sm text-gray-500">{translated.description}</div>
                      )}
                      <div className="text-xs text-gray-400">
                        {template.columns?.length || 0} {t('dataTables.columns')}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// Save as Template Modal
const SaveTemplateModal = ({ isOpen, onClose, onSave, table, t }) => {
  const [name, setName] = useState(table?.name || '');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('📊');
  const [isPublic, setIsPublic] = useState(false);

  useEffect(() => {
    if (table) {
      setName(table.name || '');
    }
  }, [table]);

  if (!isOpen) return null;

  const handleSave = () => {
    onSave({
      name,
      description,
      icon,
      is_public: isPublic,
      columns: table?.columns || []
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{t('dataTables.saveAsTemplate')}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">{t('dataTables.templateName')}</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('dataTables.tableNamePlaceholder')}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">{t('dataTables.templateDesc')}</label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('dataTables.templateDescPlaceholder')}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">{t('dataTables.templateIcon')}</label>
            <div className="flex gap-2">
              {['📊', '📋', '📦', '🚢', '🛁', '📅', '✅', '👷'].map(emoji => (
                <button
                  key={emoji}
                  onClick={() => setIcon(emoji)}
                  className={`text-2xl p-2 rounded ${icon === emoji ? 'bg-emerald-100 ring-2 ring-emerald-500' : 'hover:bg-gray-100'}`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isPublic"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              className="w-4 h-4 text-emerald-600 rounded"
            />
            <label htmlFor="isPublic" className="text-sm">
              {t('dataTables.makePublic')}
            </label>
          </div>
        </div>
        
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose}>{t('dataTables.cancel')}</Button>
          <Button onClick={handleSave} disabled={!name.trim()}>
            <Bookmark className="w-4 h-4 mr-1" />
            {t('dataTables.saveTemplate')}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Main Component
const ProjectDataTables = ({ projectId, currentUser }) => {
  const { t } = useTranslation();
  const [tables, setTables] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTableForm, setShowNewTableForm] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [selectedTableForTemplate, setSelectedTableForTemplate] = useState(null);
  const [newTableName, setNewTableName] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importMode, setImportMode] = useState('new');
  const [importTargetTable, setImportTargetTable] = useState('');
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [showLvImportModal, setShowLvImportModal] = useState(false);
  const [lvFile, setLvFile] = useState(null);
  const [lvAnalyzing, setLvAnalyzing] = useState(false);
  const [lvAnalysis, setLvAnalysis] = useState(null);
  const [lvExecutingMode, setLvExecutingMode] = useState(null);
  const [lvImportResult, setLvImportResult] = useState(null);

  // Fetch tables
  const fetchTables = useCallback(async () => {
    try {
      const response = await fetch(`${getApiUrl()}/projects/${projectId}/tables`);
      const data = await response.json();
      setTables(data);
    } catch (error) {
      console.error('Error fetching tables:', error);
    }
  }, [projectId]);

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    try {
      const response = await fetch(`${getApiUrl()}/data-templates?user_id=${currentUser.id}&include_public=true`);
      const data = await response.json();
      setTemplates(data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  }, [currentUser.id]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchTables(), fetchTemplates()]);
      setLoading(false);
    };
    loadData();
  }, [fetchTables, fetchTemplates]);

  // Create new table
  const handleCreateTable = async () => {
    if (!newTableName.trim()) return;

    try {
      const response = await fetch(`${getApiUrl()}/projects/${projectId}/tables?user_id=${currentUser.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newTableName,
          columns: [
            { name: 'Item', type: 'text', order: 0 },
            { name: 'Status', type: 'status', order: 1 }
          ]
        })
      });
      const newTable = await response.json();
      setTables([...tables, newTable]);
      setNewTableName('');
      setShowNewTableForm(false);
    } catch (error) {
      console.error('Error creating table:', error);
    }
  };

  // Create table from template
  const handleCreateFromTemplate = async (template) => {
    try {
      const response = await fetch(
        `${getApiUrl()}/projects/${projectId}/tables/from-template/${template.id}?user_id=${currentUser.id}`,
        { method: 'POST' }
      );
      const newTable = await response.json();
      setTables([...tables, newTable]);
      setShowTemplateModal(false);
    } catch (error) {
      console.error('Error creating from template:', error);
    }
  };

  // Delete table
  const handleDeleteTable = async (tableId) => {
    if (!window.confirm(t('dataTables.confirmDeleteTable', 'Are you sure you want to delete this table?'))) return;
    
    try {
      await fetch(`${getApiUrl()}/projects/${projectId}/tables/${tableId}`, {
        method: 'DELETE'
      });
      setTables(tables.filter(t => t.id !== tableId));
    } catch (error) {
      console.error('Error deleting table:', error);
    }
  };

  // Save as template
  const handleSaveAsTemplate = (table) => {
    setSelectedTableForTemplate(table);
    setShowSaveTemplateModal(true);
  };

  const handleCreateTemplate = async (templateData) => {
    try {
      const response = await fetch(`${getApiUrl()}/data-templates?user_id=${currentUser.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(templateData)
      });
      const newTemplate = await response.json();
      setTemplates([...templates, newTemplate]);
    } catch (error) {
      console.error('Error creating template:', error);
    }
  };

  // Import file
  const handleImportFile = async () => {
    if (!importFile) return;
    
    setImporting(true);
    setImportError('');
    
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('user_id', currentUser.id);
      formData.append('mode', importMode);
      formData.append('preserve_template', 'true');
      if (importMode === 'existing' && importTargetTable) {
        formData.append('existing_table_id', importTargetTable);
      }
      
      const response = await fetch(`${getApiUrl()}/projects/${projectId}/tables/import`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Import fehlgeschlagen');
      }
      
      const result = await response.json();
      
      // Refresh tables
      await fetchTables();
      
      // Close modal and reset state
      setShowImportModal(false);
      setImportFile(null);
      setImportMode('new');
      setImportTargetTable('');
      
      // Show success message
      alert(t('dataTables.importSuccess', `Erfolgreich importiert: ${result.imported_rows} Zeilen, ${result.imported_columns || result.matched_columns} Spalten`));
      
    } catch (error) {
      console.error('Import error:', error);
      setImportError(error.message || 'Import fehlgeschlagen');
    } finally {
      setImporting(false);
    }
  };

  // LV Import
  const resetLvModal = () => {
    setShowLvImportModal(false);
    setLvFile(null);
    setLvAnalysis(null);
    setLvExecutingMode(null);
    setLvImportResult(null);
  };

  const handleLvAnalyze = async () => {
    if (!lvFile) return;
    
    setLvAnalyzing(true);
    setLvImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', lvFile);
      formData.append('user_id', currentUser.id);
      
      const response = await fetch(`${getApiUrl()}/projects/${projectId}/import-lv/analyze`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'LV-Analyse fehlgeschlagen');
      }
      
      setLvAnalysis(await response.json());
    } catch (error) {
      console.error('LV Analyze error:', error);
      setLvImportResult({ status: 'error', message: error.message });
    } finally {
      setLvAnalyzing(false);
    }
  };

  const handleLvExecute = async (mode) => {
    if (!lvAnalysis) return;
    
    setLvExecutingMode(mode);
    
    try {
      const response = await fetch(`${getApiUrl()}/projects/${projectId}/import-lv/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode,
          user_id: currentUser.id,
          filename: lvAnalysis.filename,
          positions: lvAnalysis.positions
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'LV-Import fehlgeschlagen');
      }
      
      setLvImportResult(await response.json());
      await fetchTables();
    } catch (error) {
      console.error('LV Import error:', error);
      setLvImportResult({ status: 'error', message: error.message });
    } finally {
      setLvExecutingMode(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">{t('common.loading', 'Loading...')}</div>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Table className="w-5 h-5 text-emerald-600" />
          {t('dataTables.title')}
        </h2>
        <div className="flex gap-2 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowLvImportModal(true)}
            className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 border-purple-300"
          >
            <FileText className="w-4 h-4 mr-1" />
            {t('dataTables.importLv', 'LV importieren')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowImportModal(true)}
            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
          >
            <Upload className="w-4 h-4 mr-1" />
            {t('dataTables.importExcel', 'Excel/CSV Import')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowTemplateModal(true)}
          >
            <FileSpreadsheet className="w-4 h-4 mr-1" />
            {t('dataTables.fromTemplate')}
          </Button>
          <Button
            size="sm"
            onClick={() => setShowNewTableForm(true)}
          >
            <Plus className="w-4 h-4 mr-1" />
            {t('dataTables.newTable')}
          </Button>
        </div>
      </div>

      {/* New Table Form */}
      {showNewTableForm && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg border">
          <h3 className="font-medium mb-2">{t('dataTables.createTable')}</h3>
          <div className="flex gap-2">
            <Input
              value={newTableName}
              onChange={(e) => setNewTableName(e.target.value)}
              placeholder={t('dataTables.tableNamePlaceholder')}
              className="flex-1"
              onKeyDown={(e) => e.key === 'Enter' && handleCreateTable()}
            />
            <Button onClick={handleCreateTable} disabled={!newTableName.trim()}>
              {t('dataTables.create')}
            </Button>
            <Button variant="ghost" onClick={() => setShowNewTableForm(false)}>
              {t('dataTables.cancel')}
            </Button>
          </div>
        </div>
      )}

      {/* Tables List */}
      {tables.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed">
          <Table className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h3 className="font-medium text-gray-700 mb-1">{t('dataTables.noTables')}</h3>
          <p className="text-sm text-gray-500 mb-4">
            {t('dataTables.noTablesDesc')}
          </p>
          <div className="flex justify-center gap-2">
            <Button size="sm" onClick={() => setShowNewTableForm(true)}>
              <Plus className="w-4 h-4 mr-1" />
              {t('dataTables.createTable')}
            </Button>
            {templates.length > 0 && (
              <Button size="sm" variant="outline" onClick={() => setShowTemplateModal(true)}>
                <FileSpreadsheet className="w-4 h-4 mr-1" />
                {t('dataTables.fromTemplate')}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div>
          {tables.map(table => (
            <DataTableView
              key={table.id}
              table={table}
              onUpdate={fetchTables}
              onDelete={handleDeleteTable}
              onSaveAsTemplate={handleSaveAsTemplate}
              currentUser={currentUser}
            />
          ))}
        </div>
      )}

      {/* Template Modal */}
      <TemplateModal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        onSelect={handleCreateFromTemplate}
        templates={templates}
        t={t}
      />

      {/* Save Template Modal */}
      <SaveTemplateModal
        isOpen={showSaveTemplateModal}
        onClose={() => {
          setShowSaveTemplateModal(false);
          setSelectedTableForTemplate(null);
        }}
        onSave={handleCreateTemplate}
        table={selectedTableForTemplate}
        t={t}
      />

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Upload className="w-5 h-5 text-blue-600" />
                {t('dataTables.importTitle', 'Excel/CSV importieren')}
              </h3>
              <button onClick={() => {
                setShowImportModal(false);
                setImportFile(null);
                setImportError('');
              }} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* File Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                {t('dataTables.selectFile', 'Datei auswählen')}
              </label>
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => setImportFile(e.target.files[0])}
                className="w-full border rounded-lg p-2 text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('dataTables.supportedFormats', 'Unterstützt: Excel (.xlsx, .xls), CSV')}
              </p>
            </div>

            {/* Import Mode */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                {t('dataTables.importMode', 'Import-Modus')}
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="importMode"
                    value="new"
                    checked={importMode === 'new'}
                    onChange={(e) => setImportMode(e.target.value)}
                    className="text-emerald-600"
                  />
                  <div>
                    <span className="font-medium">{t('dataTables.createNewTable', 'Neue Tabelle erstellen')}</span>
                    <p className="text-xs text-gray-500">{t('dataTables.createNewTableDesc', 'Erstellt eine neue Datentabelle aus der Datei')}</p>
                  </div>
                </label>
                <label className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="importMode"
                    value="existing"
                    checked={importMode === 'existing'}
                    onChange={(e) => setImportMode(e.target.value)}
                    className="text-emerald-600"
                  />
                  <div>
                    <span className="font-medium">{t('dataTables.fillExisting', 'Bestehende Tabelle befüllen')}</span>
                    <p className="text-xs text-gray-500">{t('dataTables.fillExistingDesc', 'Fügt Daten zu einer vorhandenen Tabelle hinzu')}</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Target Table Selection (for existing mode) */}
            {importMode === 'existing' && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  {t('dataTables.selectTable', 'Tabelle auswählen')}
                </label>
                <select
                  value={importTargetTable}
                  onChange={(e) => setImportTargetTable(e.target.value)}
                  className="w-full border rounded-lg p-2 text-sm"
                >
                  <option value="">{t('dataTables.selectTablePlaceholder', '-- Tabelle wählen --')}</option>
                  {tables.map(table => (
                    <option key={table.id} value={table.id}>{table.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Info Box */}
            <div className="mb-4 p-3 bg-blue-50 rounded-lg text-sm">
              <p className="text-blue-800">
                <strong>💡 {t('dataTables.tipTitle', 'Tipp')}:</strong> {t('dataTables.tipContent', 'Das Original-Layout wird gespeichert. Beim Export können Sie "Mit Vorlage exportieren" wählen, um Formeln und Formatierung wiederherzustellen.')}
              </p>
            </div>

            {/* Error Message */}
            {importError && (
              <div className="mb-4 p-3 bg-red-50 rounded-lg text-sm text-red-700 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {importError}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setShowImportModal(false);
                setImportFile(null);
                setImportError('');
              }}>
                {t('dataTables.cancel', 'Abbrechen')}
              </Button>
              <Button 
                onClick={handleImportFile}
                disabled={!importFile || importing || (importMode === 'existing' && !importTargetTable)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    {t('dataTables.importing', 'Importiere...')}
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-1" />
                    {t('dataTables.import', 'Importieren')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* LV Import Modal */}
      {showLvImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-600" />
                {t('dataTables.lvImportTitle', 'Leistungsverzeichnis importieren')}
              </h3>
              <button onClick={resetLvModal} className="text-gray-500 hover:text-gray-700" data-testid="lv-modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {!lvImportResult && !lvAnalysis && (
              <>
                {/* File Input */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">
                    {t('dataTables.selectLvFile', 'LV-Datei auswählen (PDF oder GAEB .x81–.x86)')}
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.x81,.x82,.x83,.x84,.x85,.x86,.xml"
                    onChange={(e) => setLvFile(e.target.files[0])}
                    className="w-full border rounded-lg p-2 text-sm"
                    data-testid="lv-file-input"
                  />
                </div>

                {/* GAEB Info Box */}
                <div className="mb-3 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-800 mb-1">{t('dataTables.gaebWhatTitle', 'Was ist GAEB?')}</h4>
                  <p className="text-sm text-gray-600">
                    {t('dataTables.gaebWhatDesc', 'GAEB XML ist das deutsche Standardformat für Leistungsverzeichnisse (LV) im Bauwesen. Unterstützt werden die Austauschphasen DA 81 bis DA 86 (.x81–.x86) mit allen Positionen, Mengen und Beschreibungen.')}
                  </p>
                </div>

                {/* DATAflor Tip */}
                <div className="mb-4 p-4 bg-emerald-50 rounded-lg">
                  <h4 className="font-medium text-emerald-800 mb-1">{t('dataTables.gaebTipTitle', 'Tipp für DATAflor-Nutzer')}</h4>
                  <p className="text-sm text-emerald-700">
                    {t('dataTables.gaebTipDesc', 'Exportieren Sie Ihr LV in DATAflor als „GAEB XML Leistungsverzeichnis (DA 81)" – das enthält alle Positionen und Mengen ohne Preise und eignet sich ideal für den Task-Import.')}
                  </p>
                </div>

                {/* Info Box */}
                <div className="mb-4 p-4 bg-purple-50 rounded-lg">
                  <h4 className="font-medium text-purple-800 mb-2">🪄 {t('dataTables.lvSmartTitle', 'Intelligente Analyse')}</h4>
                  <p className="text-sm text-purple-700">
                    {t('dataTables.lvSmartDesc', 'Das LV wird analysiert. Danach wählen Sie, wie importiert werden soll: Tasks nach Kategorien, reine Datentabelle oder nur Arbeitspositionen als Tasks.')}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={resetLvModal}>
                    {t('dataTables.cancel', 'Abbrechen')}
                  </Button>
                  <Button 
                    onClick={handleLvAnalyze}
                    disabled={!lvFile || lvAnalyzing}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="lv-analyze-btn"
                  >
                    {lvAnalyzing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        {t('dataTables.lvAnalyzing', 'Analysiere LV...')}
                      </>
                    ) : (
                      <>
                        <FileText className="w-4 h-4 mr-1" />
                        {t('dataTables.lvAnalyzeBtn', 'LV analysieren')}
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}

            {/* Option Chooser */}
            {!lvImportResult && lvAnalysis && (
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">{t('dataTables.lvChooseOption', 'Import-Option wählen:')}</h4>

                <button
                  onClick={() => handleLvExecute('tasks_by_category')}
                  disabled={!!lvExecutingMode}
                  className="w-full flex items-center gap-4 p-4 border rounded-xl hover:border-purple-400 hover:bg-purple-50/50 transition-colors text-left disabled:opacity-60"
                  data-testid="lv-option-categories"
                >
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    {lvExecutingMode === 'tasks_by_category' ? (
                      <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
                    ) : (
                      <ListTree className="w-6 h-6 text-gray-700" />
                    )}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{t('dataTables.lvOptionCategories', 'Tasks nach Kategorien')}</div>
                    <div className="text-sm text-gray-500">
                      {t('dataTables.lvOptionCategoriesDesc', { count: lvAnalysis.summary.category_count, defaultValue: '{{count}} Haupt-Tasks mit Unterpunkten' })}
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => handleLvExecute('datatable')}
                  disabled={!!lvExecutingMode}
                  className="w-full flex items-center gap-4 p-4 border rounded-xl hover:border-purple-400 hover:bg-purple-50/50 transition-colors text-left disabled:opacity-60"
                  data-testid="lv-option-datatable"
                >
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    {lvExecutingMode === 'datatable' ? (
                      <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
                    ) : (
                      <Table className="w-6 h-6 text-gray-700" />
                    )}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{t('dataTables.lvOptionTable', 'Reines LV (Datentabelle)')}</div>
                    <div className="text-sm text-gray-500">
                      {t('dataTables.lvOptionTableDesc', { count: lvAnalysis.summary.total_positions, defaultValue: 'Tabelle mit {{count}} Positionen (ohne Preise)' })}
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => handleLvExecute('work_tasks')}
                  disabled={!!lvExecutingMode}
                  className="w-full flex items-center gap-4 p-4 border rounded-xl hover:border-purple-400 hover:bg-purple-50/50 transition-colors text-left disabled:opacity-60"
                  data-testid="lv-option-work"
                >
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    {lvExecutingMode === 'work_tasks' ? (
                      <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
                    ) : (
                      <Hammer className="w-6 h-6 text-gray-700" />
                    )}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{t('dataTables.lvOptionWork', 'Nur Arbeiten als Tasks')}</div>
                    <div className="text-sm text-gray-500">
                      {t('dataTables.lvOptionWorkDesc', { count: lvAnalysis.summary.work_count, defaultValue: '{{count}} echte Arbeitsaufgaben' })}
                    </div>
                  </div>
                </button>

                <div className="pt-1">
                  <Button variant="outline" size="sm" onClick={() => setLvAnalysis(null)} disabled={!!lvExecutingMode}>
                    {t('dataTables.lvBack', 'Zurück')}
                  </Button>
                </div>
              </div>
            )}

            {lvImportResult && (
              /* Result View */
              <div className="space-y-4">
                {lvImportResult.status === 'success' ? (
                  <>
                    <div className="p-4 bg-green-50 rounded-lg text-green-800" data-testid="lv-import-success">
                      <h4 className="font-semibold flex items-center gap-2">
                        <Check className="w-5 h-5" />
                        {t('dataTables.lvImportSuccess', 'LV erfolgreich importiert!')}
                      </h4>
                      <p className="text-sm mt-1">{lvImportResult.message}</p>
                    </div>

                    {lvImportResult.created_tasks?.length > 0 && (
                      <div className="border rounded-lg p-3">
                        <h5 className="font-medium mb-2 text-sm">Erstellte Tasks (Vorschau):</h5>
                        <div className="max-h-32 overflow-y-auto space-y-1">
                          {lvImportResult.created_tasks.slice(0, 5).map((task, i) => (
                            <div key={i} className="text-xs text-gray-600 truncate">
                              {task.title}
                            </div>
                          ))}
                          {lvImportResult.created_tasks.length > 5 && (
                            <div className="text-xs text-gray-400">
                              ... und {lvImportResult.created_tasks.length - 5} weitere
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="p-4 bg-red-50 rounded-lg text-red-800">
                    <h4 className="font-semibold flex items-center gap-2">
                      <AlertCircle className="w-5 h-5" />
                      {t('dataTables.lvImportError', 'Fehler beim Import')}
                    </h4>
                    <p className="text-sm mt-2">{lvImportResult.message}</p>
                  </div>
                )}

                <div className="flex justify-end">
                  <Button onClick={resetLvModal} data-testid="lv-modal-done">
                    {t('common.close', 'Schließen')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDataTables;
